import React, { useContext, useEffect, useState } from 'react';
import CwFPagination from 'components/CwFPagination';
import PropTypes from 'prop-types';
import { TABLE_COLUMNS } from 'screens/Companies/helpers/constants';
import { useHistory } from 'react-router-dom';
import CompanyTableCell from 'screens/Companies/components/CompanyList/CompanyTableRow';
import SortableTable from 'components/UiKit/SortableTable';
import Paper from 'components/Paper';
import LoadingSpinner from 'components/LoadingSpinner';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';
import { paths } from 'routes/paths';
import NoCompaniesOverlay from '../NoCompaniesOverlay';
import DeleteCompanyModal from '../DeleteCompanyModal';
import './index.css';

const getCompanyTableBodyHeight = () => Math.max(180, window.innerHeight - 396);

const CompanyList = ({
  companies,
  isLoading = false,
  memberRole,
  totalCompanies,
  companyPageNumber,
  handlePageChange,
  sortBy,
  sortDir,
  onSort
}) => {
  const [tableBodyHeight, setTableBodyHeight] = useState(
    getCompanyTableBodyHeight
  );
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const openDeleteModal = () => setDeleteModalOpen(true);
  const closeDeleteModal = () => setDeleteModalOpen(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const history = useHistory();
  const amplitude = useContext(AmplitudeContext);

  useEffect(() => {
    const updateTableBodyHeight = () =>
      setTableBodyHeight(getCompanyTableBodyHeight());
    window.addEventListener('resize', updateTableBodyHeight);
    return () => window.removeEventListener('resize', updateTableBodyHeight);
  }, []);

  const handleViewClicked = id => {
    history.push({
      pathname: `${paths.companies}/${id}/view`
    });
  };

  const handleEditClicked = id => {
    history.push({
      pathname: `${paths.companies}/${id}`
    });
  };

  const handleAction = async (action, { id, name }) => {
    const actions = {
      view: () => {
        amplitude.sendEventToAmplitude(
          amplitude.events.epic.Companies.editCompany
        );
        handleViewClicked(id);
      },
      edit: () => {
        handleEditClicked(id);
      },
      delete: () => {
        amplitude.sendEventToAmplitude(
          amplitude.events.epic.Companies.deleteCompany
        );
        openDeleteModal();
        setCompanyToDelete({ id, name });
      }
    };
    actions[action]();
  };

  return (
    <>
      {companies.length || isLoading ? (
        <Paper
          className={`companies-table ${
            isLoading ? 'companies-table--loading' : ''
          }`}
        >
          {CwFPagination(
            totalCompanies,
            companyPageNumber,
            handlePageChange,
            null,
            undefined,
            { disabled: isLoading }
          )}
          <DeleteCompanyModal
            open={deleteModalOpen}
            close={closeDeleteModal}
            companyName={companyToDelete?.name}
            companyId={companyToDelete?.id}
          />
          <div
            className="companies-table__viewport relative min-w-0 overflow-hidden"
            style={isLoading ? { height: tableBodyHeight + 48 } : undefined}
          >
            <SortableTable
              allowUnsort
              data-testid="companies-table"
              columns={TABLE_COLUMNS}
              rows={isLoading ? [] : companies}
              sortBy={sortBy}
              sortDir={sortDir}
              scrollY={tableBodyHeight}
              onSort={onSort}
              renderCell={(field, company) => (
                <CompanyTableCell
                  field={field}
                  company={company}
                  onAction={handleAction}
                />
              )}
            />
            {isLoading && (
              <div
                className="companies-table__loading z-10 flex items-center justify-center bg-white"
                data-testid="companies-list-loading"
                aria-busy="true"
              >
                <LoadingSpinner
                  message=""
                  size={72}
                  label="Loading companies"
                />
              </div>
            )}
          </div>
          {CwFPagination(
            totalCompanies,
            companyPageNumber,
            handlePageChange,
            null,
            undefined,
            { disabled: isLoading }
          )}
          <div className="mb-12"> </div>
        </Paper>
      ) : (
        <NoCompaniesOverlay
          open={companies?.length === 0}
          onClick={() => history.push(`${paths.companies}/company`)}
          memberRole={memberRole}
        />
      )}
    </>
  );
};

CompanyList.propTypes = {
  companies: PropTypes.arrayOf(
    PropTypes.shape({
      forEach: PropTypes.func,
      length: PropTypes.number
    })
  ).isRequired,
  isLoading: PropTypes.bool,
  memberRole: PropTypes.string.isRequired,
  totalCompanies: PropTypes.number.isRequired,
  companyPageNumber: PropTypes.number.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  sortBy: PropTypes.string,
  sortDir: PropTypes.string,
  onSort: PropTypes.func
};

export default CompanyList;
